import { inject, Injectable, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { CookieService } from "ngx-cookie-service";
import { ShoppingCart, ItemShoppingCart } from "../models/item-cart.model";

const EMPTY_CART: ShoppingCart = {
  items: [],
  total: 0,
  totalItems: 0,
  subTotal: 0,
  totalDiscount: 0,
};

@Injectable({
  providedIn: "root",
})
export class ShoppingCartService {
  private readonly CART_COOKIE_NAME = "shopping_cart";

  private readonly _cartOpen = signal(false);
  readonly cartOpen = this._cartOpen.asReadonly();
  /** Compat bridge — retained for consumers that subscribe via RxJS. */
  readonly cartState$ = toObservable(this._cartOpen);

  private readonly _shoppingCart = signal<ShoppingCart>({ ...EMPTY_CART });
  readonly shoppingCart = this._shoppingCart.asReadonly();
  /** Compat bridge — retained for consumers that subscribe via RxJS. */
  readonly shoppingCart$ = toObservable(this._shoppingCart);

  private readonly cookieService = inject(CookieService);

  constructor() {
    this.loadCartFromCookies();
  }

  private loadCartFromCookies() {
    const cartJson = this.cookieService.get(this.CART_COOKIE_NAME);
    if (cartJson) {
      try {
        const parsed: ShoppingCart = JSON.parse(cartJson);
        if (Array.isArray(parsed?.items)) {
          this._shoppingCart.set(parsed);
        }
      } catch {
        console.warn(
          "[CartService] Malformed cart cookie, resetting to empty cart.",
        );
        this.cookieService.delete(this.CART_COOKIE_NAME, "/");
      }
    }
  }

  openCart() {
    this._cartOpen.set(true);
  }

  closeCart() {
    this._cartOpen.set(false);
  }

  toggleCart() {
    this._cartOpen.update((v) => !v);
  }

  setShoppingCart(shoppingCart: ShoppingCart) {
    this._shoppingCart.set(shoppingCart);
    this.saveCartToCookies(shoppingCart);
  }

  addProductToCart(product: ItemShoppingCart) {
    const current = this._shoppingCart();
    const exists = current.items.some(
      (item) => item.product.id === product.product.id,
    );
    const items = exists
      ? current.items.map((item) =>
          item.product.id === product.product.id
            ? { ...item, quantity: item.quantity + product.quantity }
            : item,
        )
      : [...current.items, product];
    this.setShoppingCart({ ...current, items });
  }

  getShoppingCart(): ShoppingCart {
    return this._shoppingCart();
  }

  getTotalItemsByShoppingCart(shoppingCart: ShoppingCart): number {
    let totalItems = 0;
    if (shoppingCart.items.length === 0) return totalItems;
    shoppingCart.items.forEach((item) => {
      totalItems += item.quantity;
    });
    return totalItems;
  }

  getTotalByShoppingCart(shoppingCart: ShoppingCart): number {
    let total = 0;
    if (shoppingCart.items.length === 0) return total;
    shoppingCart.items.forEach((item) => {
      total += item.product.price * item.quantity;
    });

    return total;
  }

  getSubTotalByShoppingCart(shoppingCart: ShoppingCart): number {
    return (
      this.getTotalByShoppingCart(shoppingCart) -
      this.getTotalDiscountByShoppingCart(shoppingCart)
    );
  }

  getTotalDiscountByShoppingCart(shoppingCart: ShoppingCart): number {
    let totalDiscount = 0;
    if (shoppingCart.items.length === 0) return totalDiscount;
    shoppingCart.items.forEach((item) => {
      totalDiscount +=
        ((item.product.discountPercentage ?? 0) / 100) *
        item.product.price *
        item.quantity;
    });

    return totalDiscount;
  }

  getNumberOfProductsByShoppingCart(shoppingCart: ShoppingCart): number {
    return shoppingCart.items.length;
  }

  private saveCartToCookies(shoppingCart: ShoppingCart) {
    this.cookieService.set(this.CART_COOKIE_NAME, JSON.stringify(shoppingCart));
  }

  clearCart() {
    this._shoppingCart.set({ ...EMPTY_CART });
    this.cookieService.delete(this.CART_COOKIE_NAME, "/");
  }
}
